/**
 * Chamadas REST do bounded context `auth`.
 *
 * Contrato espelha `apps/backend/app/api/endpoints/auth.py`:
 *   POST /api/token/auth     { username, password }   → TokenResponse
 *   POST /api/token/refresh  { refresh_token }        → TokenResponse  (TD26-31)
 *   Erros: 401 { message, success: false } → ApiError
 *
 * Backend rotaciona refresh_token a cada chamada de /token/refresh, então
 * o frontend tem que persistir o novo refresh (não só o access).
 */

import { apiFetch } from '@/lib/api';
import { z } from 'zod';

const tokenResponseSchema = z.object({
  access_token: z.string().min(1),
  refresh_token: z.string().min(1),
  token_type: z.string(),
  message: z.string(),
  success: z.literal(true),
});

export type TokenResponse = z.infer<typeof tokenResponseSchema>;

export async function login(username: string, password: string): Promise<TokenResponse> {
  return apiFetch({
    path: '/api/token/auth',
    method: 'POST',
    body: { username, password },
    schema: tokenResponseSchema,
    scope: 'auth-login',
    skipAuth: true,
  });
}

/**
 * Troca refresh_token (que pode estar próximo do expiry, mas ainda válido)
 * por um novo par (access, refresh). `skipAuth: true` porque o endpoint
 * autentica via body, não via header Bearer — e o access atual pode já
 * estar expirado, o que levaria a loop de refresh.
 */
export async function refreshTokens(refreshToken: string): Promise<TokenResponse> {
  return apiFetch({
    path: '/api/token/refresh',
    method: 'POST',
    body: { refresh_token: refreshToken },
    schema: tokenResponseSchema,
    scope: 'auth-refresh',
    skipAuth: true,
  });
}
